import { apiRequest } from './apiConfig';
import { getAwardSubmissions } from './awardRoles';

const fetchAllPages = async (
  endpoint: string,
  year: number,
  pageSize = 100,
  headers = {}
) => {
  const startDate = `${year}-01-01T00:00:00.000Z`;
  const endDate = `${year}-12-31T23:59:59.999Z`;
  const yearFilter =
    `&filters[createdAt][$gte]=${encodeURIComponent(startDate)}` +
    `&filters[createdAt][$lte]=${encodeURIComponent(endDate)}`;

  // Handle query parameters properly - check if endpoint already has query params
  const separator = endpoint.includes('?') ? '&' : '?';
  const firstUrl = `${endpoint}${separator}pagination[page]=1&pagination[pageSize]=${pageSize}&pagination[withCount]=true${yearFilter}`;

  const firstResponse = await apiRequest(firstUrl, { headers });

  const firstResult = await firstResponse.json();
  const { pagination } = firstResult.meta;
  const totalPages = pagination.pageCount;

  const allData = [...firstResult.data];

  if (totalPages > 1) {
    const pagePromises = [];
    for (let page = 2; page <= totalPages; page++) {
      pagePromises.push(
        apiRequest(
          `${endpoint}${separator}pagination[page]=${page}&pagination[pageSize]=${pageSize}${yearFilter}`,
          { headers }
        ).then(response => response.json())
      );
    }

    const remainingPages = await Promise.all(pagePromises);
    remainingPages.forEach(pageResult => {
      allData.push(...pageResult.data);
    });
  }

  return {
    data: allData,
    meta: {
      pagination: {
        ...pagination,
        page: 1,
        pageSize: allData.length,
        pageCount: 1,
      },
    },
  };
};

export const fetchSubmissions = async (year: number) => {
  try {
    const result = await getAwardSubmissions(year);
    return result?.data || [];
  } catch (error) {
    console.error('Error fetching submissions:', error);
    return [];
  }
};

export const fetchVistaJudgments = async (year: number) => {
  const result = await fetchAllPages('vista-judgments', year);
  return result.data || [];
};

// Fetch only completed Vista judgments (for qualification judgment page)
export const fetchCompletedVistaJudgments = async (year: number) => {
  const endpoint = 'vista-judgments?filters[overall_summary][$eq]=Completed';
  const result = await fetchAllPages(endpoint, year);
  return result.data || [];
};

// Fetch only completed Final Vista judgments (for final judgment page).
// For 2025: also includes legacy 'Completed' (records created before the 'Final' suffix was introduced).
// For all other years: only fetches 'Completed Final'.
export const fetchCompletedFinalVistaJudgments = async (year: number) => {
  const LEGACY_YEAR = 2025;
  const endpoint =
    year === LEGACY_YEAR
      ? 'vista-judgments?filters[overall_summary][$in][0]=Completed%20Final&filters[overall_summary][$in][1]=Completed'
      : 'vista-judgments?filters[overall_summary][$eq]=Completed%20Final';
  const result = await fetchAllPages(endpoint, year);
  return result.data || [];
};

export const fetchVistaAspects = async (year: number) => {
  const result = await fetchAllPages('vista-aspects', year);
  return result.data || [];
};

export const fetchVistaRubrics = async (year: number) => {
  const result = await fetchAllPages('vista-rubrics', year);
  return result.data || [];
};
